"use client";

import { createContext, useContext, useLayoutEffect, useMemo, useRef, useSyncExternalStore, type ReactNode } from "react";
import { usePathname } from "next/navigation";
import WorkspaceDocumentHeader, { type WorkspaceDocumentHeaderProps } from "@/app/flow-test/WorkspaceDocumentHeader";
import { createWorkspaceDocumentFrameStore } from "@/lib/client/workspace-document-frame";
import { workspacePageId, type WorkspaceNavigationLabel, type WorkspacePreviewHint } from "@/lib/client/workspace-navigation-shell";
import { workspaceHeaderPreview } from "@/lib/client/workspace-header-preview";
import { useStore } from "@/app/context/StoreContext";
import { workspaceActivations } from "@/lib/client/workspace-navigation-activation";
import { matchingWorkspaceDocumentMetadata } from "@/lib/workspace-document-metadata";
import { flowCoverColor } from "@/lib/flow-cover-key";
import WorkspacePageLayout from "./WorkspacePageLayout";

type FrameStore = ReturnType<typeof createWorkspaceDocumentFrameStore<WorkspaceDocumentHeaderProps>>;
const FrameContext = createContext<FrameStore | null>(null);
const empty = () => null;

export function useWorkspaceDocumentFrame() { return useContext(FrameContext); }

/** Called only by the verified document's owner. Save queues, refs and
 * callbacks remain there; the frame merely hosts its real header DOM. */
export function useWorkspaceDocumentHeader(props: WorkspaceDocumentHeaderProps) {
  const frame = useWorkspaceDocumentFrame();
  const routeId = workspacePageId(usePathname());
  const lease = useRef({});
  useLayoutEffect(() => { frame?.publish(lease.current, props.pageId, props, routeId); }, [frame, props, routeId]);
  useLayoutEffect(() => {
    const token = lease.current;
    return () => frame?.release(token);
  }, [frame]);
  return !!frame;
}

export function WorkspaceDocumentBodyLoading({ onCancel }: { onCancel?: () => void }) {
  return <div data-workspace-body-loading role="status" aria-label="Загрузка блоков страницы" className="mt-3 pl-[68px] sm:pl-[72px]">
    <span className="sr-only">Загрузка блоков страницы</span>
    {onCancel && <button type="button" onClick={onCancel} className="mt-6 text-xs text-gray-400 underline">Отменить переход</button>}
  </div>;
}

/** One page frame across route/activation/editor loading. Verified activation
 * can supply read-only metadata before RSC; only the owner supplies actions. On a new
 * destination it falls back to its account-scoped navigation label, never
 * to the previous document. The body remains Next-owned and is not cached. */
export default function WorkspaceDocumentFrame({ identity, active, pageId, label, pending, waitingForRoute, onCancel, onPreviewHint, children }: {
  identity: string; active: boolean; pageId: string | null; label?: WorkspaceNavigationLabel | null;
  pending: boolean; waitingForRoute: boolean; onCancel: () => void; children: ReactNode;
  onPreviewHint: (pageId: string, hint: WorkspacePreviewHint) => void;
}) {
  const { stores } = useStore();
  const frame = useMemo(() => createWorkspaceDocumentFrameStore<WorkspaceDocumentHeaderProps>(), [identity]);
  const registration = useSyncExternalStore(frame.subscribe, frame.getSnapshot, empty);
  const received = useSyncExternalStore(workspaceActivations.subscribeHeader, workspaceActivations.getHeader, empty);
  const early = matchingWorkspaceDocumentMetadata(received, identity, pageId);
  const live = !pending && registration?.pageId === pageId ? registration.value : null;
  // Remember local header edits for the next click too, without retaining any
  // picture, document or editable callbacks in the navigation list.
  useLayoutEffect(() => {
    if (live && pageId) onPreviewHint(pageId, {
      description: live.description, hasCover: !!live.cover,
      coverColor: flowCoverColor(live.cover) ? live.cover : undefined,
      ...(live.changedAt && Number.isFinite(live.changedAt) ? { updated_at: new Date(live.changedAt).toISOString() } : {}),
    });
  }, [live, pageId, onPreviewHint]);
  const props: WorkspaceDocumentHeaderProps = live ?? (early ? {
    ...early, canEdit: false, pending: true, metadataReady: true, storeChip: early.store,
  } : { ...workspaceHeaderPreview(label, stores), pageId: pageId ?? "" });
  // List hint works on the first click, before activation. Verified metadata
  // (including explicit false after cover removal) always takes precedence.
  const hasCover = live ? !!live.cover : early ? early.hasCover : !!label?.hasCover;
  const imageCover = hasCover && !flowCoverColor(props.cover);
  return <FrameContext.Provider value={active ? frame : null}>
    {active ? <main data-workspace-document-frame className="flow-font relative min-h-screen bg-[#f7f5f0] text-[#37352f]"
      style={{ "--sidebar-w": "0px", "--topbar-h": "0px", "--nav-top": "44px", "--flow-page-bg": "#f7f5f0" } as React.CSSProperties}>
      {props.cover && imageCover && <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 z-0 h-[600px] overflow-hidden">
        {/* eslint-disable-next-line @next/next/no-img-element -- existing page background */}
        <img src={props.cover} alt="" decoding="async" className="h-full w-full object-cover" />
        <div className="absolute inset-x-0 bottom-0 h-56" style={{ background: "linear-gradient(to bottom, rgba(247,245,240,0) 0%, rgba(247,245,240,0.7) 55%, #f7f5f0 100%)" }} />
      </div>}
      <div className="relative z-10 px-6 pb-24 pt-[68px]">
        <WorkspacePageLayout fullWidth={props.fullWidth} imageCover={imageCover}>
          <WorkspaceDocumentHeader {...props} />
          <div hidden={waitingForRoute} inert={waitingForRoute}>{children}</div>
          {waitingForRoute && <WorkspaceDocumentBodyLoading onCancel={onCancel} />}
        </WorkspacePageLayout>
      </div>
    </main> : children}
  </FrameContext.Provider>;
}
